import { usePublicProjectReader, publicQueryKey, projectReaderGet } from '@/lib/public-project-reader';
import { useMemo } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete, ApiClientError } from '@/lib/api';
import type {
    Task,
    TaskDetail,
    TaskAssignee,
    CreateTaskInput,
    UpdateTaskInput,
    TaskStatus,
    TaskPriority,
} from '@/lib/schemas';

// ============================================
// QUERY KEYS
// ============================================

export const taskKeys = {
    all: ['tasks'] as const,
    lists: () => [...taskKeys.all, 'list'] as const,
    list: (projectId: string, filters?: TaskFilters) =>
        [...taskKeys.lists(), projectId, filters] as const,
    myTasks: (filters?: MyTasksFilters) => [...taskKeys.all, 'my', filters] as const,
    details: () => [...taskKeys.all, 'detail'] as const,
    detail: (id: string) => [...taskKeys.details(), id] as const,
};

// ============================================
// TYPES
// ============================================

export interface TaskFilters {
    status?: TaskStatus[];
    priority?: TaskPriority[];
    assignee?: string;
    parentId?: string | null;
    search?: string;
    dueDateFrom?: string;
    dueDateTo?: string;
    sortBy?: 'order' | 'title' | 'status' | 'priority' | 'dueDate' | 'createdAt' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
    includeSubtasks?: boolean;
    // t43 (R13): customFields accepts either the legacy
    // `Record<fieldId, string[]>` shape OR the new per-type filter
    // shape `Record<fieldId, { type, op, value?, value2? }>`. The
    // task-filters UI sends the new shape; the backend accepts
    // both.
    customFields?: Record<string, unknown>;
}

interface TaskPage {
    tasks: Task[];
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
}

const PROJECT_TASK_PAGE_SIZE = 40;

export interface MyTasksFilters {
    status?: TaskStatus[];
    priority?: TaskPriority[];
    search?: string;
    dueDateFrom?: string;
    dueDateTo?: string;
    sortBy?: 'title' | 'status' | 'priority' | 'dueDate' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
}

// Task with project info for My Tasks view
export interface TaskWithProject extends Task {
    project: {
        id: string;
        name: string;
        color: string | null;
        iconEmoji: string | null;
        imageUrl: string | null;
    };
}

// ============================================
// TASKS HOOKS
// ============================================

export function useTasks(projectId: string, filters?: TaskFilters) {
    const reader = usePublicProjectReader();
    const query = useInfiniteQuery({
        queryKey: publicQueryKey(reader, taskKeys.list(projectId, filters)),
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams();

            if (filters?.status && filters.status.length > 0) {
                params.set('status', filters.status.join(','));
            }
            if (filters?.priority && filters.priority.length > 0) {
                params.set('priority', filters.priority.join(','));
            }
            if (filters?.assignee) {
                params.set('assignee', filters.assignee);
            }
            if (filters?.parentId !== undefined) {
                params.set('parentId', filters.parentId === null ? 'null' : filters.parentId);
            }
            if (filters?.search) {
                params.set('search', filters.search);
            }
            if (filters?.dueDateFrom) {
                params.set('dueDateFrom', filters.dueDateFrom);
            }
            if (filters?.dueDateTo) {
                params.set('dueDateTo', filters.dueDateTo);
            }
            if (filters?.sortBy) {
                params.set('sortBy', filters.sortBy);
            }
            if (filters?.sortOrder) {
                params.set('sortOrder', filters.sortOrder);
            }
            if (filters?.includeSubtasks) {
                params.set('includeSubtasks', 'true');
            }
            // Pass custom field filters as JSON
            if (filters?.customFields && Object.keys(filters.customFields).length > 0) {
                params.set('customFields', JSON.stringify(filters.customFields));
            }
            params.set('limit', PROJECT_TASK_PAGE_SIZE.toString());
            params.set('offset', pageParam.toString());

            const queryString = params.toString();
            const endpoint = queryString
                ? `/projects/${projectId}/tasks?${queryString}`
                : `/projects/${projectId}/tasks`;

            const data = await projectReaderGet<TaskPage>(reader, endpoint);
            return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage) => (
            lastPage.hasMore ? lastPage.offset + lastPage.tasks.length : undefined
        ),
        refetchInterval: reader ? 10000 : false,
        enabled: !!projectId,
        staleTime: 30 * 1000, // 30 seconds
    });

    const data = useMemo(() => {
        if (!query.data) return undefined;
        const firstPage = query.data.pages[0];
        const lastPage = query.data.pages[query.data.pages.length - 1];
        return {
            tasks: query.data.pages.flatMap((page) => page.tasks),
            total: firstPage?.total ?? 0,
            hasMore: lastPage?.hasMore ?? false,
        };
    }, [query.data]);

    return { ...query, data };
}

// Fetch all tasks assigned to the current user across all projects
export function useMyTasks(filters?: MyTasksFilters) {
    return useQuery({
        queryKey: taskKeys.myTasks(filters),
        queryFn: async () => {
            const params = new URLSearchParams();

            if (filters?.status && filters.status.length > 0) {
                params.set('status', filters.status.join(','));
            }
            if (filters?.priority && filters.priority.length > 0) {
                params.set('priority', filters.priority.join(','));
            }
            if (filters?.search) {
                params.set('search', filters.search);
            }
            if (filters?.dueDateFrom) {
                params.set('dueDateFrom', filters.dueDateFrom);
            }
            if (filters?.dueDateTo) {
                params.set('dueDateTo', filters.dueDateTo);
            }
            if (filters?.sortBy) {
                params.set('sortBy', filters.sortBy);
            }
            if (filters?.sortOrder) {
                params.set('sortOrder', filters.sortOrder);
            }

            const queryString = params.toString();
            const endpoint = queryString ? `/tasks/my?${queryString}` : `/tasks/my`;

            const data = await apiGet<{ tasks: TaskWithProject[]; total: number; stats: { assigned: number; dueToday: number; completedThisWeek: number } }>(endpoint);
            return data;
        },
        staleTime: 30 * 1000, // 30 seconds
    });
}

export function useTask(taskId: string) {
    const reader = usePublicProjectReader();
    return useQuery({
        queryKey: publicQueryKey(reader, taskKeys.detail(taskId)),
        queryFn: async () => {
            const data = await projectReaderGet<TaskDetail>(reader, `/tasks/${taskId}`);
            return data;
        },
        refetchInterval: reader ? 10000 : false,
        enabled: !!taskId,
    });
}

export function useCreateTask(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateTaskInput) => {
            return apiPost<Task>(`/projects/${projectId}/tasks`, data);
        },
        onSuccess: () => {
            // Invalidate ALL task list queries for this project (regardless of filters)
            queryClient.invalidateQueries({ queryKey: ['tasks', 'list', projectId] });
            // Also invalidate myTasks in case task is assigned to current user
            queryClient.invalidateQueries({ queryKey: ['tasks', 'my'] });
        },
    });
}

export function useUpdateTask(taskId: string, projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: UpdateTaskInput) => {
            return apiPatch<Task>(`/tasks/${taskId}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
            // Invalidate ALL task list queries for this project (regardless of filters)
            queryClient.invalidateQueries({ queryKey: ['tasks', 'list', projectId] });
            // Also invalidate myTasks
            queryClient.invalidateQueries({ queryKey: ['tasks', 'my'] });
        },
    });
}

// Version of useUpdateTask that accepts taskId and projectId with the mutation
export function useUpdateTaskDynamic() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ taskId, projectId, data }: { taskId: string; projectId: string; data: UpdateTaskInput }) => {
            return apiPatch<Task>(`/tasks/${taskId}`, data);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.taskId) });
            // Invalidate ALL task list queries for this project (regardless of filters)
            queryClient.invalidateQueries({ queryKey: ['tasks', 'list', variables.projectId] });
            // Also invalidate myTasks
            queryClient.invalidateQueries({ queryKey: ['tasks', 'my'] });
        },
    });
}

export function useUpdateTaskOrder(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            taskId,
            order,
            parentId,
        }: {
            taskId: string;
            order: number;
            parentId?: string | null;
        }) => {
            return apiPatch<Task>(`/tasks/${taskId}/order`, { order, parentId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) });
        },
    });
}

export function useDeleteTask(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (taskId: string) => {
            return apiDelete<{ message: string }>(`/tasks/${taskId}`);
        },
        retry: (failureCount, error) => {
            if (failureCount >= 1) return false;
            return !(error instanceof ApiClientError) || error.statusCode >= 500;
        },
        onSuccess: () => {
            // Invalidate ALL task list queries for this project (regardless of filters)
            queryClient.invalidateQueries({ queryKey: ['tasks', 'list', projectId] });
            // Also invalidate myTasks
            queryClient.invalidateQueries({ queryKey: ['tasks', 'my'] });
        },
    });
}

// ============================================
// SUBTASK HOOKS
// ============================================

export function useCreateSubtask(parentTaskId: string, projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateTaskInput) => {
            return apiPost<Task>(`/tasks/${parentTaskId}/subtasks`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskKeys.detail(parentTaskId) });
            queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) });
        },
    });
}

// ============================================
// ASSIGNEE HOOKS
// ============================================

export function useAddAssignee(taskId: string, projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (userId: string) => {
            return apiPost<TaskAssignee>(`/tasks/${taskId}/assignees`, { userId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
            queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) });
        },
    });
}

export function useRemoveAssignee(taskId: string, projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (userId: string) => {
            return apiDelete<{ message: string }>(`/tasks/${taskId}/assignees/${userId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
            queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) });
        },
    });
}

// ============================================
// BATCH UPDATE HOOK
// ============================================

export function useBatchUpdateTasks(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (updates: { taskId: string; data: UpdateTaskInput }[]) => {
            const results = await Promise.all(
                updates.map(({ taskId, data }) => apiPatch<Task>(`/tasks/${taskId}`, data))
            );
            return results;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: taskKeys.list(projectId) });
        },
    });
}
